import { useEffect, useMemo } from "react";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { SupportedSession, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { getLatestAddressAnswer, getLatestBridgeEligibilityAnswer, isBookingBlockedByEligibility } from "../../utils";

export interface AppointmentBookingFieldContentProps
  extends Omit<TellescopeFormFieldBaseProps<"Appointment Booking">, "title"> {
  session: SupportedSession;
  enduserId: string;
  userIds?: string[];
  portalURL?: string;
  organizationSubdomain?: string;
  priorResponses?: TellescopeFieldResponse[];
  setBookedAppointmentUrl?: (bookedAppointmentUrl: string | undefined) => void;
}

export const AppointmentBookingFieldContent = ({
  value,
  onChange,
  className,
  session,
  enduserId,
  options,
  priorResponses,
  setBookedAppointmentUrl,
}: AppointmentBookingFieldContentProps) => {
  const { t } = useTranslation("forms");

  const addressState = priorResponses?.length ? getLatestAddressAnswer(priorResponses)?.state : undefined;

  const {
    data: bookingInfoData,
    isLoading,
    error,
  } = useTellescopeQuery({
    model: "form_fields",
    method: "booking_info",
    request: {
      bookingPageId: options?.bookingPageId ?? "",
      enduserId,
      ...(addressState ? { enduserFields: { state: addressState } } : {}),
    },
    session,
    enabled: !!options?.bookingPageId,
  });

  const bridgeUserIds = useMemo(() => {
    if (!options?.useBridgeEligibilityResult || !priorResponses?.length) return undefined;
    return getLatestBridgeEligibilityAnswer(priorResponses)?.userIds;
  }, [options?.useBridgeEligibilityResult, priorResponses]);

  // The booking page must not be shown rather than fall back to an unfiltered one: an eligibility
  // check that named no providers means none are bookable, and omitting userIds would offer every
  // provider. The field stays non-blocking — see isBookingBlockedByEligibility.
  const hasNoEligibleProviders = isBookingBlockedByEligibility(
    { type: "Appointment Booking", options },
    priorResponses ?? []
  );

  const iframeURL = useMemo(() => {
    const base = bookingInfoData?.bookingURL ?? "";
    if (!base) return base;

    const params: string[] = [];
    if (bridgeUserIds?.length) params.push(`userIds=${bridgeUserIds.join(",")}`);
    if (options?.holdAppointmentMinutes) params.push(`holdAppointmentMinutes=${options.holdAppointmentMinutes}`);
    if (!params.length) return base;

    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${params.join("&")}`;
  }, [bookingInfoData?.bookingURL, bridgeUserIds, options?.holdAppointmentMinutes]);

  const bookingURL = bookingInfoData?.bookingURL;
  const entropy = bookingInfoData?.entropy;

  const expectedOrigin = useMemo(() => {
    if (!bookingURL) return undefined;
    try {
      return new URL(bookingURL).origin;
    } catch {
      return undefined;
    }
  }, [bookingURL]);

  // Listen for postMessage from iframe indicating successful booking
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // The nonce only disambiguates between embeds; the origin is what establishes that the
      // message actually came from the booking page we loaded.
      if (expectedOrigin && event.origin !== expectedOrigin) return;

      // Stashed for form.redirectToBookedAppointmentOnSubmit to consume at submit. Arrives as its own
      // message, on its own timing, so it cannot be read off the Booking Success payload below.
      if (event.data?.type === "Join Link" && typeof event.data?.link === "string" && event.data.link) {
        setBookedAppointmentUrl?.(event.data.link);
        return;
      }

      if (event.data?.type !== "Booking Success") return;
      if (typeof event.data?.bookedEventId !== "string") return;
      // The portal echoes an empty nonce for booking URLs minted before entropy existed, so an
      // absent or empty value cannot be attributed and is accepted; a populated one must match.
      if (event.data.entropy && event.data.entropy !== entropy) return;

      onChange(event.data.bookedEventId);
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onChange, expectedOrigin, entropy, setBookedAppointmentUrl]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <p>{t("Loading appointment booking...")}</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={className} style={{ padding: "2rem", color: "red" }}>
        <p>
          {t("Error: {{errorMessage}}", {
            errorMessage: error instanceof Error ? error.message : t("Failed to load appointment booking"),
          })}
        </p>
      </div>
    );
  }

  // Show success message if appointment was booked
  if (value) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ marginBottom: "1rem", color: "green", fontWeight: "bold" }}>
          {t("✓ Appointment Successfully Booked")}
        </div>
        <p style={{ fontSize: "0.875rem", color: "#666" }}>{t("Event ID: {{id}}", { id: value })}</p>
      </div>
    );
  }

  if (hasNoEligibleProviders) {
    return (
      <div className={className} style={{ padding: "2rem", textAlign: "center" }}>
        <p>{t("No eligible users found for booking")}</p>
      </div>
    );
  }

  // Show iframe when URL is ready
  if (iframeURL) {
    return (
      <div className={className}>
        <iframe
          src={iframeURL}
          title="Appointment Booking"
          style={{
            width: "100%",
            height: "800px",
            scale: "0.85",
            border: "none",
          }}
        />
      </div>
    );
  }

  return null;
};
